/**
 * Wire shapes for the `ai-agent-*` services, transcribed from the installed
 * package (`@intrasync/ai-agent-api` 0.0.21) rather than guessed:
 *
 *   dist/services/catalog/agents/agents.schema.js
 *   dist/services/conversation/conversations/conversations.schema.js
 *   dist/services/conversation/messages/messages.schema.js
 *   dist/services/runs/runs/runs.schema.js
 *   dist/services/tools/tool-invocations/tool-invocations.schema.js
 *   dist/services/runs/runs/runs.class.js   (RUN_STREAM_EVENTS + payloads)
 *
 * JSON columns (`content`, `sources`, `usage`, `input`, `output`) are decoded by
 * each service's external resolver, so they arrive as real objects/arrays on the
 * wire — never as strings.
 */

/** The `ai-agent-*` service paths this module reads. */
export const AI_AGENT_AGENTS_PATH = "ai-agent-agents";
export const AI_AGENT_CONVERSATIONS_PATH = "ai-agent-conversations";
export const AI_AGENT_MESSAGES_PATH = "ai-agent-messages";
export const AI_AGENT_RUNS_PATH = "ai-agent-runs";
export const AI_AGENT_TOOL_INVOCATIONS_PATH = "ai-agent-tool-invocations";

/**
 * HER NAME — HQ's office manager. The one constant a person reads on the web;
 * the API's twin is `OFFICE_MANAGER_DISPLAY_NAME`
 * (packages/api/src/services/ai-agent/office-manager.seed.ts), and
 * officeManagerName.parity.test.ts holds the two equal, so a rename is these
 * two lines.
 */
export const OFFICE_MANAGER_NAME = "Rosie";

/** Who she is, in one line: what the chat says under her name. */
export const OFFICE_MANAGER_TAGLINE = `${OFFICE_MANAGER_NAME} is HQ's office manager: she knows who is working on which client and what each client needs, and she only tells you what you're allowed to see.`;

/** The first-run hint: a few questions she answers well. */
export const OFFICE_MANAGER_EXAMPLES: readonly string[] = [
  "Where does Acme stand right now?",
  "What is Dana working on this week?",
  "What did we decide in the last meeting with Acme?",
  "Who can see the Acme account?",
];

/**
 * Catalog name of the seeded agent row (services/ai-agent/office-manager.seed.ts
 * `OFFICE_MANAGER_AGENT_NAME`). KEPT as 'isa-hq': it is a wire format the API
 * upserts and this page resolves her by, not a name anyone reads — the
 * existing row, and every conversation filed under it, became Rosie's in place.
 */
export const OFFICE_MANAGER_AGENT_NAME = "isa-hq";

/** The task key whose route decides which provider the agent loop dials. */
export const AGENT_CHAT_TASK_KEY = "agent.chat";

/**
 * Provider types the agent run loop can drive — mirrors the API's
 * AGENT_CAPABLE_PROVIDERS (hq-llm-client.ts). A route to anything else is
 * refused by the API and falls back to the azure.openai config block.
 */
export const AGENT_CAPABLE_PROVIDER_TYPES: readonly string[] = [
  "azureOpenAI",
  "openai",
  "anthropic",
  "gemini",
];

/**
 * One neutral content block. The package validates `content` leniently (an array
 * of open objects) because the block shape is owned by its `ContentBlock`
 * contract, so this is modelled as one open interface rather than a union — an
 * unknown `type` must render as "unsupported", never crash the transcript.
 *
 * Kinds the run loop actually emits: `text`, `tool_use`, `tool_result`.
 */
export interface AgentContentBlock {
  type: string;
  /** `text` blocks. */
  text?: string;
  /** `tool_use` / `tool_result` correlation id. */
  toolCallId?: string;
  /** `tool_use`: the catalog tool name. */
  name?: string;
  /** `tool_use`: the arguments the model chose. */
  input?: unknown;
  /** `tool_result`: the stringified handler output. */
  content?: string;
  /** `tool_result`: set when the handler threw. */
  isError?: boolean;
}

export type AgentMessageRole = "system" | "user" | "assistant" | "tool";
export type AgentMessageStatus = "streaming" | "complete" | "error";

export interface AgentTokenUsage {
  promptTokens?: number | null;
  completionTokens?: number | null;
  totalTokens?: number | null;
}

/**
 * One entry of a message's `sources`. The office manager's final answer carries
 * the HQ records its tools cited (`kind`/`id`/`label`) and the sources they
 * could not read (`kind: "could-not-read"`) — written by the API's run loop
 * hook, so a citation is in the data, not only in the model's words.
 */
export interface AgentMessageSource {
  source?: string;
  kind?: string;
  id?: string;
  label?: string;
}

export interface AgentMessage {
  id: string;
  conversationId: string;
  runId?: string | null;
  role: AgentMessageRole;
  content: AgentContentBlock[];
  sources?: AgentMessageSource[] | null;
  toolCallId?: string | null;
  name?: string | null;
  usage?: AgentTokenUsage | null;
  modelId?: string | null;
  status: AgentMessageStatus;
  createdAt: string;
  createdBy?: number | null;
}

export type AgentConversationStatus = "active" | "archived";

export interface AgentConversation {
  id: string;
  agentId: string;
  userId?: number | null;
  title?: string | null;
  status: AgentConversationStatus;
  /** Maintained server-side by the message projection; null until the first turn. */
  lastMessageAt: string | null;
  messageCount: number;
  createdAt: string;
  createdBy?: number | null;
}

export type AgentRunStatus =
  | "pending"
  | "running"
  | "awaiting_tool"
  | "completed"
  | "failed"
  | "cancelled";

export interface AgentRun {
  id: string;
  conversationId: string;
  agentId: string;
  traceId: string;
  status: AgentRunStatus;
  modelId?: string | null;
  output?: unknown;
  usage?: AgentTokenUsage | null;
  /**
   * Sanitized failure JSON — `{"message","name","code","status"}` written by the
   * package's `sanitizeErrorForColumn`. This is where the "no Azure OpenAI
   * target for the agent" message surfaces, verbatim, when credentials are
   * missing.
   */
  error?: string | null;
  startedAt: string | null;
  endedAt: string | null;
  createdAt: string;
}

export type AgentToolInvocationStatus = "succeeded" | "failed";

export interface AgentToolInvocation {
  id: string;
  runId: string;
  toolName: string;
  toolCallId: string;
  input?: unknown;
  output?: unknown;
  status: AgentToolInvocationStatus | string;
  error?: string | null;
  durationMs?: number | null;
  createdAt: string;
}

export interface AgentDefinition {
  id: string;
  name: string;
  description?: string | null;
  defaultModelId?: string | null;
  /** Decoded on read; an EMPTY allowlist means no tool reaches the model at all. */
  toolNames?: string[] | null;
  isActive: boolean;
}

/**
 * Transient `run.stream.*` envelope. Every payload carries
 * `{ runId, conversationId, companyId }`; `chunk` rides on `run.stream.chunk`
 * and `toolName`/`toolCallId` on `run.stream.tool`.
 */
export interface RunStreamEvent {
  runId: string;
  conversationId: string;
  companyId?: string;
  chunk?: string;
  toolName?: string;
  toolCallId?: string;
}

/** The four transient events the runs service declares in its `events` list. */
export const RUN_STREAM_EVENTS = [
  "run.stream.start",
  "run.stream.chunk",
  "run.stream.tool",
  "run.stream.end",
] as const;

export type RunStreamEventName = (typeof RUN_STREAM_EVENTS)[number];

// ── Display model ──────────────────────────────────────────────────────────

export type ChatToolCallStatus = "running" | "succeeded" | "failed";

/** One tool call, joined from the `tool_use` block + `tool_result` + audit row. */
export interface ChatToolCall {
  toolCallId: string;
  name: string;
  input?: unknown;
  status: ChatToolCallStatus;
  durationMs?: number | null;
  /** Stringified handler output (from the `tool_result` block). */
  resultPreview?: string;
  error?: string | null;
}

export type ChatItemKind = "user" | "assistant" | "tools";

/** One rendered row in the transcript. */
export interface ChatItem {
  key: string;
  kind: ChatItemKind;
  text?: string;
  createdAt: string;
  modelId?: string | null;
  usage?: AgentTokenUsage | null;
  toolCalls?: ChatToolCall[];
  /** An answer's HQ records ("client ACM", "meeting 412, …"), from the message's `sources`. */
  cited?: string[];
  /** The sources an answer's tools could not read — said, never shown as nothing. */
  couldNotRead?: string[];
  /** True for the not-yet-persisted, streaming assistant bubble. */
  pending?: boolean;
}
